import { DoubleVerifyModuleSettings } from '@mbrtargeting/metatag-config-types';
import { SlotEventDetail, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { isArray, isHTMLElement, isNonEmptyString, isString } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectAdserverGamSettings, selectCommonSettings, selectModuleSettings, selectPositionSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { CONFIG_RESOLVER } from '../../../loader/token.js';
import { renderTemplate } from '../../../utils/render-template.js';
import { ADSLOT_CONTROLLER, GLOBAL_TARGETING } from '../../token.js';
import { getIdsForAdSlot, setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { constructAdUnitPath } from '../../utils/construct-adunit-path.js';
import { AdslotAddon, AdslotControllerInterface } from '../adslot-controller/adslot-controller-interface.js';
import { LifecycleAction, LifecycleState } from '../adslot-controller/adslot-lifecycle-state.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';

const log = logger.logGroup({ prefix: 'DoubleVerify' });

export interface DoubleVerifyIdPackage {
    [indexName: string]: string;

    tagId: string;
    advertiserId: string;
    orderId: string;
    lineItemId: string;
    creativeId: string;
    zone: string;
    adUnitPath: string;
    width_height: string;
    pts_sid: string;
    slotName: string;
    deviceClass: 'MEW' | 'Desktop';
    viewabilityId: string;
}

const removeMcmChildId = (adUnitPath: string) => {
    return adUnitPath.replace(/,[0-9]+\//, '/');
};

@injectionTarget()
export class DoubleverifyTrackingAdSlotAddon implements AdslotAddon {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;
    @inject(ADSLOT_CONTROLLER) private adslotController!: AdslotControllerInterface;

    private config: DoubleVerifyModuleSettings | undefined;

    constructor() {
        const config = this.config = this.configResolver.get(selectModuleSettings('DOUBLEVERIFY'));
        if (!config?.active) {
            log.debug('measurement disabled');
            return;
        }
        this.adslotController.insertAddon(this);
    }

    // will be applied to each existing and newly created adslot
    registerOnAdslot(adslot: Adslot) {
        adslot.lifecycle.onAction(LifecycleAction.RESET, async () => {
            setIdsForAdSlot(adslot.slotName, {
                source: 'doubleverify',
                values: {
                    viewabilityId: '', //overwrite former value when slot is reloaded, so detection will trigger anew, but keep value history
                }
            })
        });

        const slotRenderedListener = ({ detail }: CustomEvent<SlotEventDetail>) => {
            if (adslot.slotName === detail.slotName) {
                log.debug('%s: setup visibility measurement.', [detail.slotName.toUpperCase()]);
                this.measureAdSlot(adslot);
            }
        }
        adslot.lifecycle.onState(LifecycleState.DEREGISTERED, async () => {
            window.removeEventListener(SystemSlotEvent.SDG_SLOT_RENDERED, slotRenderedListener);
        });
        window.addEventListener(SystemSlotEvent.SDG_SLOT_RENDERED, slotRenderedListener);
    }

    /**
     * triggered by adSlot addon, tries to determine how many DOM nodes to measure on the slot and attaches a visibility measurement script to each
     * @param currentSlot
     */
    public measureAdSlot(currentSlot: Adslot) {
        const domNodesToMeasure = this.determineNodesToMeasure(currentSlot);
        const baseIds = this.createIdPackage(currentSlot);
        const isSingularNode = domNodesToMeasure.length <= 1;

        domNodesToMeasure.forEach(({ node, name }) => {
            const targetNode = node;
            const dvScript = document.createElement('script');
            const defaultNodeId = Math.round(Math.random() * 100000) + '_' + currentSlot.slotName + 'visibility';
            const targetNodeId = targetNode.getAttribute('id') || defaultNodeId;
            const currentEntryIds = Object.assign({}, baseIds, { 'target': targetNodeId });

            currentEntryIds.creativeName = `${currentEntryIds.creativeId}_${currentEntryIds.formatName}${(!isSingularNode) ? '_' : ''}${name}`;
            if (!targetNode.hasAttribute('id')) targetNode.setAttribute('id', targetNodeId); //if id is missing, set one
            targetNode.setAttribute('data-dv-view', targetNodeId); //set a data attribute, so DV can find the node
            dvScript.src = renderTemplate(this.config!.url, currentEntryIds);
            targetNode.parentNode?.appendChild(dvScript);
        });
        log.info('%s: has detected %s element/s to measure.', [currentSlot.slotName.toUpperCase(), domNodesToMeasure.length]);
    }

    createIdPackage(currentSlot: Adslot): DoubleVerifyIdPackage {
        const { slotName } = currentSlot;
        const { isMobileSlot = false } = this.configResolver.get(selectPositionSettings(slotName)) || {};
        const { WtNumberMobile = '', WtNumberDesktop = '' } = this.configResolver.get(selectCommonSettings());
        const { desktopAdUnit, mobileAdUnit } = this.configResolver.get(selectAdserverGamSettings())?.config || {};
        const pts_sid = this.globalTargeting.get('pts_sid')?.toArray()?.[0];
        const pageType = currentSlot.getPageType();
        const zone = currentSlot.getZone() + ((pageType) ? '_' + pageType : '');

        //data passed by creativeSource on ad delivery, but not guaranteed. Handle fallbacks.
        let { advertiserId, orderId, lineItemId, creativeId, adUnitPath, viewabilityId, realSize, size } = getIdsForAdSlot(slotName);

        //MCMId undesired in Doubleverify data . Fallback to constructed 4444 adUnitPath if data is missing from IdsForAdSlot.
        const adUnitPathWithoutMCMId = isString(adUnitPath) ? removeMcmChildId(adUnitPath) : constructAdUnitPath({ slotName }, { addMcmId: false });

        //websiteName should have syntax "/networkCode/websiteName", fallback if data is missing from IdsForAdSlot
        const websiteNameWithNetworkId = /(\/\w+\/[\w-_\.]+)\//g.exec(adUnitPathWithoutMCMId)?.[1];
        const websiteName = encodeURIComponent(websiteNameWithNetworkId ? websiteNameWithNetworkId : isMobileSlot ? mobileAdUnit : desktopAdUnit);


        // Only YL master creatives passes "realSize", because GAM is delivering it via 1x1.
        const width_x_height = realSize?.toString() || size?.toString() || 'error';

        // if viewabilityId was passed, use it. Otherwise, map viewabilityId through size
        if (!isNonEmptyString(viewabilityId)) {
            viewabilityId = this.getViewabilityIdBySize(width_x_height);
        }

        return {
            tagId: this.config!.tagId,
            advertiserId: isString(advertiserId) ? advertiserId : '0',
            orderId: isString(orderId) ? orderId : '0',
            lineItemId: isString(lineItemId) ? lineItemId : '0',
            creativeId: isString(creativeId) ? creativeId : '0',
            zone,
            websiteName,
            adUnitPath: encodeURIComponent(adUnitPathWithoutMCMId),
            width_height: isString(size) ? size : '0',
            pts_sid,
            slotName,
            deviceClass: (isMobileSlot) ? 'MEW' : 'Desktop',
            viewabilityId,
            formatName: viewabilityId, // TODO: remove when all macros in global.json are migrated to viewabilityId
            referrer: encodeURIComponent(location.protocol + '//' + location.host + location.pathname), //without url queries
            wtId: (isMobileSlot) ? WtNumberMobile : WtNumberDesktop,
            mtVersion: 'mt2',
        };
    }

    /**
     * decides which DOMNodes on the slot need to measured. This can be just one, or multiple.
     * @param currentSlot
     */
    determineNodesToMeasure(currentSlot: Adslot) {
        const slotNodes = currentSlot.getVisibilityNodes();
        let nodes: Array<{ name: string, node: HTMLElement }> = [];

        if (slotNodes.length > 0) {
            slotNodes.forEach((entry) => nodes.push(entry));
        } else {
            //legacy, some templates use setIdsForAdSlot to pass property "domNodes" to measure. Use as fallback if slot.addVisibilityNode() was not used. todo remove when sunsetting metatag1
            const idData = getIdsForAdSlot(currentSlot.slotName);
            nodes = isArray(idData.domNodes) ? idData.domNodes.filter(isHTMLElement).map(node => ({
                name: '',
                node
            })) : [];
        }
        if (nodes.length === 0) {
            const defaultNode = currentSlot.adserverContainer.querySelector('iframe[id*="google_ads_iframe"]');
            if (isHTMLElement(defaultNode)) nodes.push({ name: '', node: defaultNode });
        }
        return nodes;
    }

    getViewabilityIdBySize(size: string) {
        let viewabilityId;

        switch (size) {
            case '728x90':
            case '728x91':
            case '728x92':
            case '728x93':
            case '728x94':
            case '728x95':
                viewabilityId = 'SuperBanner';
                break;
            case '728x180':
                viewabilityId = 'DoubleLeaderboard';
                break;
            case '770x250':
            case '770x251':
            case '770x252':
            case '771x250':
            case '800x250':
            case '800x251':
            case '800x252':
            case '970x250':
            case '970x251':
            case '970x252':
            case '970x253':
                viewabilityId = 'Billboard';
                break;
            case '970x255':
                viewabilityId = 'QualityFooter';
                break;
            case '468x60':
                viewabilityId = 'Fullbanner';
                break;
            case '120x600':
                viewabilityId = 'Skyscraper_120';
                break;
            case '160x600':
                viewabilityId = 'Skyscraper_160';
                break;
            case '200x600':
                viewabilityId = 'Skyscraper_200';
                break;
            case '300x600':
            case '300x601':
            case '301x600':
                viewabilityId = 'HalfpageAd';
                break;
            case '300x50':
            case '320x50':
                viewabilityId = 'MobileAd_6-1';
                break;
            case '300x75':
            case '320x75':
                viewabilityId = 'MobileAd_4-1';
                break;
            case '300x100':
            case '320x100':
                viewabilityId = 'MobileAd_3-1';
                break;
            case '300x150':
            case '320x150':
                viewabilityId = 'MobileAd_2-1';
                break;
            case '300x250':
            case '300x251':
            case '300x252':
            case '300x253':
            case '300x254':
            case '300x255':
            case '300x257':
            case '301x250':
            case '302x250':
            case '303x250':
                viewabilityId = 'MediumRectangle';
                break;
            case '610x180':
            case '610x181':
            case '610x182':
                viewabilityId = 'TO_Contentbar_mini';
                break;
            case '610x250':
                viewabilityId = 'Google_Contentbox';
                break;
            default:
                viewabilityId = 'x-unknown-' + size;
        }
        return viewabilityId;
    }
}
